// Dashboard button order. Pure — no I/O. Archived and non-visible types are
// dropped first; there is no cap (D-26).
//
// 1. pinned, ascending pinOrder (ties → useCount desc → name asc)
// 2. Unpinned + dueStatus === "overdue", descending overdueByDays
// 3. Rest: useCount desc → lastLoggedAt desc → name asc

import type { ActivityType, Visibility } from "./types.js";
import type { RecurrenceStats } from "./recurrence.js";

export interface DashboardViewer {
  userId: string;
  isHouseholdOwner: boolean;
}

export interface VisibleItem {
  visibility: Visibility;
  ownerUserId?: string;
}

export function canSeeOnDashboard(viewer: DashboardViewer, item: VisibleItem): boolean {
  if (item.visibility === "shared") return true;
  if (viewer.isHouseholdOwner) return true;
  return item.ownerUserId === viewer.userId;
}

function nameCmp(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

function pinTieBreak(a: ActivityType, b: ActivityType): number {
  if (b.useCount !== a.useCount) return b.useCount - a.useCount;
  return nameCmp(a.name, b.name);
}

function restTieBreak(a: ActivityType, b: ActivityType): number {
  if (b.useCount !== a.useCount) return b.useCount - a.useCount;
  const aLast = a.lastLoggedAt ?? "";
  const bLast = b.lastLoggedAt ?? "";
  if (aLast !== bLast) return bLast.localeCompare(aLast);
  return nameCmp(a.name, b.name);
}

export function orderDashboardTypes(
  types: ActivityType[],
  statsByTypeId: Record<string, RecurrenceStats>,
  viewer: DashboardViewer,
): ActivityType[] {
  const visible = types.filter((t) => !t.archived && canSeeOnDashboard(viewer, t));

  const pinned = visible
    .filter((t) => t.pinned)
    .sort((a, b) => {
      const ao = a.pinOrder ?? Number.POSITIVE_INFINITY;
      const bo = b.pinOrder ?? Number.POSITIVE_INFINITY;
      if (ao !== bo) return ao - bo;
      return pinTieBreak(a, b);
    });

  const unpinned = visible.filter((t) => !t.pinned);

  const overdue = unpinned
    .filter((t) => statsByTypeId[t.activityTypeId]?.dueStatus === "overdue")
    .sort((a, b) => {
      const ad = statsByTypeId[a.activityTypeId]?.overdueByDays ?? 0;
      const bd = statsByTypeId[b.activityTypeId]?.overdueByDays ?? 0;
      if (ad !== bd) return bd - ad;
      return restTieBreak(a, b);
    });

  const rest = unpinned
    .filter((t) => statsByTypeId[t.activityTypeId]?.dueStatus !== "overdue")
    .sort(restTieBreak);

  return [...pinned, ...overdue, ...rest];
}
